// Copyright (c) 2020 embyt GmbH. All rights reserved.

import cloneDeep from "lodash.clonedeep";

import {
  ActionGraphCommand,
  ActionType,
  IAction,
  IActionChargerCommand,
  IActionNewChargerData,
  IActionNewReport,
  IActionNewReportData,
  IActionSortTable,
  IActionTrend,
  IActionTypeChargingSessionList,
  IActionTypeConfirmPopup,
  IActionTypeEdit,
  IActionTypeAuthFail,
  IActionTypeForgotPassword,
  IActionTypeLogin,
  IActionTypeMfaSetup,
  IActionTypeSetPeriod,
  IActionTypeSignUp,
  IActionTypeSignUpResult,
  IActionTypeZoom,
} from "actions";
import { IState } from "states/states";
import {
  ConditionViewMode,
  IStateGui,
  INITIAL_STATE,
  ModalPanel,
  IPeriodData,
  OcppCmdState,
  SiteSettingsArea,
  UserSettingsArea,
} from "states/guistates";
import {
  getDeletionBoundary,
  getGraphBoundaryDates,
  getGraphBoundaryDatesFromPeriod,
  getPeriodStart,
  makeCommandSeqNr,
  setInputField,
} from "guiutils";
import TrendWindowModel from "model/trendwindow";
import { isSet } from "shared/utils/utils";
import { DataState, MainPanel, ForegroundPanel, TimePeriod } from "types";
import { IStateRouter } from "states/routerstates";
import { ImportFile } from "graphql/api";
import { DateTime } from "luxon";

// uses webpack definitions
declare const IS_CLOUD: boolean;

function deriveNewGraphStartDate(state: IStateGui, rootState: IState, cmd: IActionTypeSetPeriod) {
  const period = state[cmd.identifier];
  let newXZoom = cmd.timePeriod ?? period.timePeriod ?? TimePeriod.CUSTOM;
  let newCustomDaysSelected = period.customDaysSelected;

  const { selectedStart, graphStart, graphEnd, timezoneOffset } = getGraphBoundaryDatesFromPeriod(
    period!,
    rootState.site.tzOffset,
    newXZoom,
  );
  const curGraphDates = { startDate: graphStart, endDate: graphEnd, timezoneOffset };
  let newStartDate: number | undefined = selectedStart;

  if (
    [ActionGraphCommand.BACKWARD, ActionGraphCommand.FORWARD].includes(cmd.command) &&
    period.timePeriod !== TimePeriod.ALL &&
    curGraphDates.startDate // this is the case if timePeriod is not all
  ) {
    const customShift = period.customDaysSelected ?? 1;

    const shiftPara =
      period.timePeriod === TimePeriod.DAY ? { days: 1 }
      : period.timePeriod === TimePeriod.WEEK ? { weeks: 1 }
      : period.timePeriod === TimePeriod.MONTH ? { months: 1 }
      : period.timePeriod === TimePeriod.CUSTOM ? { days: customShift }
      : { years: 1 };

    if (cmd.command === ActionGraphCommand.BACKWARD) {
      newStartDate = Math.round(
        DateTime.fromSeconds(curGraphDates.startDate).minus(shiftPara).toSeconds(),
      );
    } else {
      newStartDate = Math.round(
        DateTime.fromSeconds(curGraphDates.startDate).plus(shiftPara).toSeconds(),
      );

      // check whether we reached the end of the data range
      const endGraphDates = getGraphBoundaryDates(
        undefined,
        undefined,
        rootState.site.tzOffset,
        period.timePeriod,
      );

      if (endGraphDates.startDate && newStartDate >= endGraphDates.startDate) {
        newStartDate = endGraphDates.startDate;
      }
    }
  } else if (cmd.command === ActionGraphCommand.SET_PERIOD) {
    newXZoom = cmd.timePeriod!;
    newStartDate = cmd.selectedDate ?? newStartDate;
    newCustomDaysSelected = undefined;
    if (newXZoom !== TimePeriod.CUSTOM) {
      // keep zoomed date on zoom-in
      if (isSet((period as IPeriodData).zoomMin)) {
        newStartDate = getPeriodStart(
          (period as IPeriodData).zoomMin! / 1000,
          newXZoom,
          curGraphDates.timezoneOffset,
        );
      }
      // check whether we got before the start of the data range
      const dataBoundaries = TrendWindowModel.getDataBoundaries(rootState, cmd.identifier);
      const endGraphDates = getGraphBoundaryDates(
        dataBoundaries.min,
        dataBoundaries.max,
        rootState.site.tzOffset,
        newXZoom,
      );

      if (newStartDate && endGraphDates.startDate && newStartDate < endGraphDates.startDate) {
        newStartDate = endGraphDates.startDate;
      }
    } else {
      // CUSTOM PERIOD-SELECT - use given start/end-time
      newStartDate = cmd.selectedDate;
      newCustomDaysSelected = cmd.customDaysSelected ?? 1;
    }
  } else if (cmd.command === ActionGraphCommand.END) {
    if (newXZoom !== TimePeriod.CUSTOM) {
      newStartDate = undefined;
    }
    const periodPara =
      period.timePeriod === TimePeriod.DAY ? "day"
      : period.timePeriod === TimePeriod.WEEK ? "week"
      : period.timePeriod === TimePeriod.MONTH ? "month"
      : period.timePeriod === TimePeriod.CUSTOM ? "day"
      : "year";

    if (
      newXZoom === TimePeriod.CUSTOM &&
      isSet(curGraphDates.startDate) &&
      isSet(curGraphDates.endDate)
    ) {
      // keep the custom span and shift it so that it ends with the current period
      const customShift = Math.round(
        (curGraphDates.endDate - curGraphDates.startDate) / 60 / 60 / 24,
      );
      const periodEnd = DateTime.now()
        .startOf(periodPara)
        .plus({ [periodPara]: 1 });
      newStartDate = periodEnd.minus({ days: customShift }).toSeconds();
    } else {
      newStartDate = Math.round(DateTime.now().startOf(periodPara).toSeconds());
    }
  } else if (cmd.command === ActionGraphCommand.START) {
    const minTimestamp = TrendWindowModel.getDataBoundaries(rootState, cmd.identifier).min;
    if (isSet(minTimestamp)) {
      newStartDate = getPeriodStart(minTimestamp, period.timePeriod, curGraphDates.timezoneOffset);
    }
    const peroiodPara =
      period.timePeriod === TimePeriod.DAY ? "day"
      : period.timePeriod === TimePeriod.WEEK ? "week"
      : period.timePeriod === TimePeriod.MONTH ? "month"
      : period.timePeriod === TimePeriod.CUSTOM ? "day"
      : "year";

    if (isSet(newStartDate)) {
      newStartDate = Math.round(
        DateTime.fromSeconds(newStartDate).startOf(peroiodPara).toSeconds(),
      );
    }
  } else if (cmd.command === ActionGraphCommand.SET_DATE) {
    const { selectedStart, timePeriod } = getGraphBoundaryDatesFromPeriod(
      cmd,
      rootState.site.tzOffset,
    );
    newStartDate = selectedStart;
    newXZoom = timePeriod;
    newCustomDaysSelected = cmd.customDaysSelected;
  } else {
    console.error("unexpected graph command", cmd);
  }
  // perform a final check whether the data are available in the given aggregation type
  const { deletionBoundary, aggregationBoundary } = getDeletionBoundary(rootState);
  // switch zoom? or adjust start date?
  if (cmd.identifier !== "chargingSessionFilter" && newStartDate) {
    // check deletion boundary
    if (isSet(deletionBoundary) && newStartDate < deletionBoundary.toSeconds()) {
      newStartDate = deletionBoundary.toSeconds();
    }
    if (
      isSet(aggregationBoundary) &&
      newStartDate < aggregationBoundary.toSeconds() &&
      [TimePeriod.DAY, TimePeriod.WEEK].includes(newXZoom)
    ) {
      newXZoom = TimePeriod.MONTH;
    }
  }
  return { newXZoom, newStartDate, newCustomDaysSelected };
}

export default function guiReducer(
  state: IStateGui,
  action: IAction<unknown>,
  rootState: IState,
): IStateGui {
  switch (action.type) {
    case ActionType.ACT_SIGNUP_SUCC:
      // a resent confirmation code keeps the confirmation panel open
      if ((action.payload as IActionTypeSignUpResult).confirmed) {
        state.foregroundPanel = undefined;
      }
      return state;
    case ActionType.ACT_INIT_DONE: // intentional fall-through
    case ActionType.ACT_CANCEL_PWD_CHANGE: // intentional fall-through
    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_SUCC: // intentional fall-through
    case ActionType.ACT_CHANGE_AWSUSER_SUCC:
      // we've got success answer from last call -> check in which state we are...
      state.foregroundPanel = undefined;
      return state;
    case ActionType.ACT_CHANGE_PASSWORD:
      state.foregroundPanel = ForegroundPanel.PASSWORD_CHANGE;
      return state;
    case ActionType.ACT_NAVIGATION_COLLAPSE:
      // toggle navigation bar collapse status
      state.navigationIsOpen = !state.navigationIsOpen;
      return state;
    case ActionType.ACT_NAVIGATION_DROPDOWN_COLLAPSE: {
      const payload = action.payload as MainPanel;
      state.isDropdownOpen[payload] = !state.isDropdownOpen[payload];
      return state;
    }

    case ActionType.ACT_NAVIGATION: {
      const navigationArgs = action.payload as IStateRouter;
      const newPanel = navigationArgs.panel;

      // close potentially open drop-down navigation menu and confirm modal
      state.navigationIsOpen = false;
      state.confirmModal.isOpen = false;

      if (newPanel !== MainPanel.CHARGERDETAIL) {
        state.searchFilterText = undefined;
      }

      state.foregroundPanel = undefined;

      // reset zoom in dashboard
      if (newPanel === MainPanel.DASHBOARD) {
        state.trendPeriod.zoomMin = undefined;
        state.trendPeriod.zoomMax = undefined;
      }
      // reset charging session filter in showing all sessions
      if (newPanel === MainPanel.CHARGINGSESSIONLIST) {
        state.chargingSessionFilter.timePeriod = TimePeriod.MONTH;
      } else {
        // reset chargingSessionFilter - chargerName on any other panel than CHARGINGSESSIONLIST
        state.chargingSessionFilter.chargerName = undefined;
      }
      if (newPanel === MainPanel.CHARGERDETAIL) {
        // reset potential pending ocpp command state
        state.chargerCmd.state = OcppCmdState.NONE;
      }
      return state;
    }

    case ActionType.ACT_SITE_SETTINGS_AREA_TOGGLE: {
      const payload = action.payload as SiteSettingsArea[];
      state.openedSiteSettingsArea = payload;
      return state;
    }

    case ActionType.ACT_USER_SETTINGS_AREA_TOGGLE: {
      const payload = action.payload as UserSettingsArea[];
      state.openedUserSettingsArea = payload;
      return state;
    }

    case ActionType.ACT_EDIT_VALUE: {
      const edit = action.payload as IActionTypeEdit;
      const editArray = Array.isArray(edit) ? edit : [edit];
      editArray.forEach((curItem) => {
        setInputField(state, curItem.name, curItem.value);
      });
      return state;
    }

    case ActionType.ACT_SELECT_SITE:
      // new site selected or just reloading cached site?
      if (isSet(action.payload)) {
        // remove data that shall not be persisted over potentially different sites
        state.rfidList = [];
        state.chargingSessionFilter = cloneDeep(INITIAL_STATE.chargingSessionFilter);
        state.reportLinks = {};
        state.selectedIoType = undefined;
        state.reportData = {
          requested: 0,
        };
      }
      // remove data also not persisted over site reload
      state.openedSiteSettingsArea = undefined;
      return state;

    case ActionType.ACT_FILTER_CHARGERNAME: {
      const filter = action.payload as string;
      state.chargingSessionFilter.chargerName = filter;
      return state;
    }

    case ActionType.ACT_MODAL_CANCEL:
      state.modal.panel = ModalPanel.NONE;
      // ensure data update on next opening
      state.modalInput = undefined;
      state.modalOutput = undefined;
      state.modalReport = undefined;
      state.modalCharger = undefined;
      state.modalRfid = undefined;
      state.modalTrendData = undefined;
      return state;

    case ActionType.ACT_CONFIRM_POPUP: {
      const payload = action.payload as IActionTypeConfirmPopup;
      state.confirmModal = {
        ...payload,
        isOpen: true,
      };
      return state;
    }

    case ActionType.ACT_CONFIRM_CANCEL:
      state.confirmModal.isOpen = false;
      return state;

    case ActionType.ACT_SELECT_FILTERS:
      state.modal = {
        panel: ModalPanel.FILTER_SELECTION,
        dataState: DataState.EMPTY,
      };
      return state;

    case ActionType.ACT_SELECT_CATEGORY:
      if (state.modal.panel === ModalPanel.FILTER_SELECTION) {
        state.modal = { panel: ModalPanel.NONE, dataState: DataState.SAVE_SUCC };
      }
      return state;

    case ActionType.ACT_ZOOM_GRAPH: {
      const data = action.payload as IActionTypeZoom;
      state[data.identifier].zoomMin = data.minX;
      state[data.identifier].zoomMax = data.maxX;
      return state;
    }
    case ActionType.ACT_EDIT_PERIOD:
      state.modal.panel = ModalPanel.PERIOD_SELECTION;
      return state;

    case ActionType.ACT_CHANGE_PERIOD: {
      const cmd = action.payload as IActionTypeSetPeriod | undefined;
      if (isSet(cmd)) {
        const periodSetting = state[cmd.identifier] as IPeriodData;
        if (isSet(periodSetting)) {
          const { newXZoom, newStartDate, newCustomDaysSelected } = deriveNewGraphStartDate(
            state,
            rootState,
            cmd,
          );
          periodSetting.timePeriod = newXZoom;
          periodSetting.selectedDate = newStartDate;
          periodSetting.customDaysSelected = newCustomDaysSelected;

          // reset zoom if we modify the graph
          periodSetting.zoomMin = undefined;
          periodSetting.zoomMax = undefined;
        }
      }

      // close Period-Filter (if open)
      if ([ModalPanel.PERIOD_SELECTION].includes(state.modal.panel)) {
        state.modal.panel = ModalPanel.NONE;
        state.modalTariff = undefined;
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      return state;
    }

    case ActionType.ACT_SELECT_TIMESTAMP:
      state.selectedTimestamp = action.payload as number;
      return state;

    case ActionType.ACT_EDIT_INPUT: {
      const id = action.payload as number;
      state.modal = {
        panel: ModalPanel.INPUT,
        dataState: DataState.EMPTY,
      };
      state.modalInput = id;
      return state;
    }
    case ActionType.ACT_CHANGE_INPUT_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_INPUT_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.INPUT) {
        state.modal.panel = ModalPanel.NONE;
        state.modalInput = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_INPUT_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_EDIT_OUTPUT: {
      const id = action.payload as number;
      state.modal = {
        panel: ModalPanel.OUTPUT,
        dataState: DataState.EMPTY,
      };
      state.modalOutput = id;
      return state;
    }
    case ActionType.ACT_CHANGE_OUTPUT_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.OUTPUT) {
        state.modal.panel = ModalPanel.NONE;
        state.modalOutput = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_EDIT_TRENDDATA:
      state.modal = {
        panel: ModalPanel.TREND,
        dataState: DataState.EMPTY,
      };
      state.modalTrendData = action.payload as IActionTrend;
      return state;
    case ActionType.ACT_CHANGE_TREND_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_TREND_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.TREND) {
        state.modal.panel = ModalPanel.NONE;
        state.modalTrendData = undefined;
      }
      return state;
    case ActionType.ACT_CHANGE_TREND_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_OCPP_SESSIONS_SUCC:
      // add new rfid cards to list
      (action.payload as IActionTypeChargingSessionList).forEach((curItem) => {
        if (curItem.rfid && !state.rfidList.includes(curItem.rfid)) {
          state.rfidList.push(curItem.rfid);
        }
      });
      return state;

    case ActionType.ACT_EDIT_REPORT:
      state.modal = {
        panel: ModalPanel.REPORT_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalReport = action.payload as number | undefined;
      return state;
    case ActionType.ACT_CHANGE_REPORT_SUCC:
      if (state.modal.panel === ModalPanel.REPORT_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalReport = undefined;
      }
      return state;

    case ActionType.ACT_EDIT_TRENDMONITOR:
      state.modal = {
        panel: ModalPanel.TRENDMONITOR_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalTrendMonitor = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC:
      if (state.modal.panel === ModalPanel.TRENDMONITOR_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalReport = undefined;
      }
      return state;

    case ActionType.ACT_CREATE_REPORT_SUCC:
      state.reportLinks[action.payload as number] = {
        requested: Date.now(),
      };
      return state;
    case ActionType.ACT_CREATE_REPORT_DATA_SUCC:
      state.reportData = {
        ...state.reportData!,
        requested: new Date().getTime(),
      };
      return state;
    case ActionType.ACT_NEW_REPORT: {
      const payload = action.payload as IActionNewReport;
      if (state.reportLinks[payload.reportId!]) {
        state.reportLinks[payload.reportId!].link = payload.link;
        state.reportLinks[payload.reportId!].error = payload.error;
      } else {
        console.warn("setting unknown report link", payload);
      }
      return state;
    }
    case ActionType.ACT_CREATE_REPORT_REQ: {
      state.reportData = {
        requested: new Date().getTime(),
        // make sure data and error is removed on a
        // new query, to show "Data loading..."
        data: undefined,
        error: undefined,
      };
      return state;
    }
    case ActionType.ACT_NEW_REPORT_DATA: {
      const payload = action.payload as IActionNewReportData;
      // ensure screen updates
      state.reportData = {
        ...state.reportData!,
        data: payload.data,
        error: payload.error,
      };

      return state;
    }
    case ActionType.ACT_CREATE_REPORT_FAIL:
      // remove potentially pending report generations, ensuring a screen update
      state.reportLinks = Object.fromEntries(
        Object.entries(state.reportLinks).filter(([, curLink]) => isSet(curLink.link)),
      );
      state.reportData = {
        ...state.reportData!,
        data: undefined,
        error: (action.payload as string | undefined) ?? "Error generating report",
      };
      return state;

    case ActionType.ACT_GET_FILEUPLOADS_SUCC:
      state.fileUploads = action.payload as ImportFile[];
      return state;

    case ActionType.ACT_EDIT_CHARGER:
      state.modal = {
        panel: ModalPanel.CHARGER_SETTINGS,
        dataState: DataState.EMPTY,
      };
      state.modalCharger = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_SAVE_OCPP_CHARGERS_SUCC:
      state.modal.dataState = DataState.SAVE_SUCC;
      if (state.modal.panel === ModalPanel.CHARGER_SETTINGS) {
        state.modal.panel = ModalPanel.NONE;
        state.modalCharger = undefined;
      }
      return state;

    case ActionType.ACT_EDIT_RFID:
      state.modal = {
        panel: ModalPanel.RFID,
        dataState: DataState.EMPTY,
      };
      state.modalRfid = {
        id: typeof action.payload === "number" ? action.payload : undefined,
        addedrfid: typeof action.payload === "string" ? action.payload : undefined,
        checkResult: undefined,
      };
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_SUCC:
      if (state.modal.panel === ModalPanel.RFID) {
        state.modal.panel = ModalPanel.NONE;
        state.modalRfid = undefined;
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      return state;
    case ActionType.ACT_SAVE_OCPP_RFID_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      if (isSet(state.modalRfid)) {
        state.modalRfid.saveError = action.payload as string | undefined;
      }
      return state;

    case ActionType.ACT_EDIT_TARIFF:
      state.modal = {
        panel: ModalPanel.TARIFF,
        dataState: DataState.EMPTY,
      };
      state.modalTariff = action.payload as number | undefined;
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_REQ:
      state.modal.dataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_SUCC:
      // saving tariff settings
      if ([ModalPanel.TARIFF, ModalPanel.GLOBAL_TARIFF].includes(state.modal.panel)) {
        state.modal.panel = ModalPanel.NONE;
        state.modalTariff = undefined;
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      // saving tariff association of rfid or charger
      if ([ModalPanel.RFID, ModalPanel.CHARGER_SETTINGS].includes(state.modal.panel)) {
        state.modal.dataState = DataState.SAVE_SUCC;
      }
      return state;
    case ActionType.ACT_SAVE_OCPP_TARIFF_FAIL:
      state.modal.dataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_NEW_GLOBAL_TARIFF:
      // open global tariff selection box
      state.modal = {
        panel: ModalPanel.GLOBAL_TARIFF,
        dataState: DataState.EMPTY,
      };
      return state;

    case ActionType.ACT_SORT_TABLE: {
      const payload = action.payload as IActionSortTable;
      state.tableSort[payload.table] = {
        column: payload.column,
        isSortAscending: payload.isSortAscending,
      };
      return state;
    }

    case ActionType.ACT_CHECK_OCPP_RFIDS_REQ:
      if (state.modalRfid) {
        state.modalRfid.checkResult = undefined;
        // remove save error indication if we modify the input
        if (state.modal.dataState === DataState.SAVE_FAIL) {
          state.modal.dataState = DataState.EMPTY;
        }
      }
      return state;
    case ActionType.ACT_CHECK_OCPP_RFIDS_SUCC:
      if (state.modalRfid) {
        state.modalRfid.checkResult = action.payload as string | undefined;
      }
      return state;
    case ActionType.ACT_CHECK_OCPP_RFIDS_FAIL:
      if (state.modalRfid) {
        state.modalRfid.checkResult = "";
      }
      return state;

    case ActionType.ACT_TRIGGER_CHARGER_CMD_REQ:
      state.chargerCmd.state = OcppCmdState.NEW;
      state.chargerCmd.hasError = false;
      state.chargerCmd.result = undefined;
      state.chargerCmd.timestampMs = (action.payload as IActionChargerCommand).timestampMs;
      state.chargerCmd.chargerId = (action.payload as IActionChargerCommand).chargerId;
      return state;
    case ActionType.ACT_TRIGGER_CHARGER_CMD_SUCC:
      state.chargerCmd.state = OcppCmdState.ISSUED;
      return state;
    case ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL:
      state.chargerCmd.hasError = true;
      state.chargerCmd.result = action.payload as string;
      return state;
    case ActionType.ACT_NEW_OCPP_DATA: {
      const payload = action.payload as IActionNewChargerData;
      if (
        payload.chargerId === state.chargerCmd.chargerId &&
        payload.commandSeqNr === makeCommandSeqNr(state.chargerCmd.timestampMs)
      ) {
        // ocpp command result received
        // there may be race conditions between messages, so update only if we have no result yet
        if (!state.chargerCmd.hasError) state.chargerCmd.hasError = payload.commandHasError ?? true;
        if (!state.chargerCmd.result) state.chargerCmd.result = payload.commandResult;
        // state increment only if successful
        if (payload.commandHasError === false) {
          state.chargerCmd.state =
            !isSet(payload.commandResult) && state.chargerCmd.state !== OcppCmdState.CONFIRMED ?
              OcppCmdState.SENT
            : OcppCmdState.CONFIRMED;
        }
      }
      return state;
    }

    case ActionType.ACT_START_TRANSACTION_POPUP:
      state.modal = {
        panel: ModalPanel.START_TRANSACTION,
        dataState: DataState.EMPTY,
      };
      return state;

    case ActionType.ACT_PERFORM_FILE_UPLOAD_REQ:
      state.lastFileUploadError = undefined;
      return state;
    case ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL:
    case ActionType.ACT_GET_FILEUPLOADS_FAIL:
      state.lastFileUploadError = (action.payload as string | undefined) ?? "Error uploading file";
      return state;

    case ActionType.ACT_LOGIN_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_CONFIRM_REQ: // intentional fall-through
    case ActionType.ACT_SIGNUP_RESEND_REQ:
      state.login.email = (action.payload as IActionTypeLogin | IActionTypeSignUp).username;
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      // keep the sign-up panel open during sign-up requests, so failures can be shown there
      if (action.type === ActionType.ACT_LOGIN_REQ) {
        state.foregroundPanel = undefined;
      }
      return state;

    case ActionType.ACT_LOGOUT_REQ:
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      state.foregroundPanel = undefined;
      return state;

    case ActionType.ACT_AUTH_FAIL: {
      const payload = action.payload as IActionTypeAuthFail;
      state.login.userStateAWSFeedback = payload.awsError;
      state.login.userStateErrorInfo = payload.errorText;
      return state;
    }

    case ActionType.ACT_SIGNUP:
      state.login.email = action.payload as string | undefined;
      state.login.userStateAWSFeedback = undefined;
      state.login.userStateErrorInfo = undefined;
      state.foregroundPanel = isSet(state.login.email) ? ForegroundPanel.SIGNUP : undefined;
      return state;

    case ActionType.ACT_SIGNUP_CONFIRM: {
      const payload = action.payload as IActionTypeSignUp;
      state.login.email = payload.username;
      state.foregroundPanel = ForegroundPanel.SIGNUP_CONFIRM;
      if (payload.keepErrorState === false) {
        state.login.userStateAWSFeedback = undefined;
        state.login.userStateErrorInfo = undefined;
      }
      return state;
    }

    case ActionType.ACT_MFA_CODE_REQUIRED:
      state.login.email = action.payload as string;
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      state.foregroundPanel = ForegroundPanel.MFA_CODE;
      return state;

    case ActionType.ACT_LOGIN_SUCC:
      // also closes the MFA code entry form after a successful second login step
      state.foregroundPanel = undefined;
      return state;

    case ActionType.ACT_MFA_CONFIRM_REQ: // intentional fall-through
    case ActionType.ACT_MFA_SETUP_REQ: // intentional fall-through
    case ActionType.ACT_MFA_VERIFY_REQ: // intentional fall-through
    case ActionType.ACT_MFA_DISABLE_REQ:
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      return state;

    case ActionType.ACT_MFA_SETUP_SUCC: {
      const payload = action.payload as IActionTypeMfaSetup;
      state.login.mfaSetupUri = payload.setupUri;
      state.login.mfaSecret = payload.secret;
      return state;
    }

    case ActionType.ACT_MFA_SETUP_CANCEL:
      state.login.mfaSetupUri = undefined;
      state.login.mfaSecret = undefined;
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      return state;

    case ActionType.ACT_MFA_STATUS_SUCC:
      state.login.mfaEnabled = action.payload as boolean;
      // enrollment finished or aborted -> drop the setup data
      state.login.mfaSetupUri = undefined;
      state.login.mfaSecret = undefined;
      return state;

    case ActionType.ACT_BACK_TO_LOGIN:
      state.login.email = action.payload as string;
      state.foregroundPanel = undefined;
      state.login.userStateErrorInfo = undefined;
      state.login.userStateAWSFeedback = undefined;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD:
      state.login.email = action.payload as string;
      state.foregroundPanel = ForegroundPanel.FORGOT_PASSWORD;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD_CODE_REQ: {
      const payload = action.payload as IActionTypeForgotPassword;
      state.login.email = payload.username;
      return state;
    }

    case ActionType.ACT_FORGOT_PASSWORD_CODE_SUCC:
      state.foregroundPanel = ForegroundPanel.PASSWORD_CONFIRM;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ: {
      const payload = action.payload as IActionTypeLogin;
      state.login.email = payload.username;
      return state;
    }

    case ActionType.ACT_CHANGE_USER_FAIL:
      state.login.userErrorText = action.payload as string;
      return state;

    case ActionType.ACT_SEARCH_FILTER_TEXT_CHANGED:
      state.searchFilterText = action.payload as string;
      return state;

    case ActionType.ACT_SELECT_CONDITIONVIEWMODE:
      state.conditionViewMode = action.payload as ConditionViewMode;
      return state;

    default:
      return state;
  }
}
